import React, { useState, useMemo } from 'react';
import { UserProfile } from '../types';
import { Utensils, Flower2, Leaf, Sparkles, ChevronRight, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface DailyRitualsProps {
    userProfile: UserProfile | null;
    onSelect: (query: string) => void;
}

type ModuleType = 'DIET' | 'YOGA' | 'REMEDY';

interface SmartPrompt {
    text: string;
    season?: string[]; // 'winter', 'summer', etc.
    location?: string; // 'WA', etc.
    constitution?: string[]; // 'vata', 'pitta', 'kapha'
}

const DailyRituals: React.FC<DailyRitualsProps> = ({ userProfile, onSelect }) => {
    const [activePopup, setActivePopup] = useState<ModuleType | null>(null);
    const [popupInput, setPopupInput] = useState('');

    // --- Smart Prompts Logic ---
    // Mock Location/Date for now (as per requirements example: WA, Feb)
    const isWinter = new Date().getMonth() <= 2 || new Date().getMonth() >= 11;
    const userLocation = "WA";

    const getPrompts = (type: ModuleType): string[] => {
        const rawDosha = userProfile?.vikriti_dosha || userProfile?.prakriti_dosha;
        const dosha = rawDosha?.split('-')[0].toLowerCase();
        const prompts: string[] = [];

        if (type === 'DIET') {
            if (dosha === 'vata') prompts.push("Warming foods for Vata balance");
            if (dosha === 'pitta') prompts.push("Cooling meals for Pitta energy");
            if (dosha === 'kapha') prompts.push("Light, spicy foods to energize Kapha");

            if (isWinter && userLocation === "WA") {
                prompts.push("Balance late-winter dampness");
            }
            prompts.push("Seasonal superfoods");
        } else if (type === 'YOGA') {
            if (dosha === 'vata') prompts.push("Grounding flow for stability");
            else prompts.push("Morning stretches for energy");
            prompts.push("Release neck tension");
        } else if (type === 'REMEDY') {
            prompts.push("Natural sleep aids");
            prompts.push("Boost immunity naturally");
        }

        return prompts.slice(0, 2); // Return top 2
    };

    const handleCardClick = (type: ModuleType) => {
        setActivePopup(type);
        setPopupInput('');
    };

    const handleSubmitPopup = () => {
        if (!popupInput.trim()) return;
        onSelect(popupInput); // Pass to ChatInterface
        setActivePopup(null);
    };

    const renderPopupContent = () => {
        switch (activePopup) {
            case 'DIET':
                return {
                    title: "Kitchen Pharmacy",
                    question: "Let’s look at your Kitchen Pharmacy. How is your digestion today?",
                    icon: <Utensils size={24} className="text-orange-500" />,
                    color: "orange"
                };
            case 'YOGA':
                return {
                    title: "Body Wisdom",
                    question: "Ready to align? Tell me where you feel tension in your body.",
                    icon: <Flower2 size={24} className="text-pink-500" />,
                    color: "pink"
                };
            case 'REMEDY':
                return {
                    title: "Botanical Rx",
                    question: "Nature's Medicine Cabinet. What symptoms are bothering you?",
                    icon: <Leaf size={24} className="text-blue-500" />,
                    color: "blue"
                };
            default:
                return null;
        }
    };

    const popupData = renderPopupContent();

    return (
        <div className="w-full max-w-4xl mx-auto px-4 mt-8 mb-12">
            <div className="text-center mb-8">
                <h3 className="text-sm font-bold text-sage-600 uppercase tracking-widest mb-2 flex items-center justify-center gap-2">
                    <Sparkles size={14} className="text-yellow-500" /> Daily Healing Rituals
                </h3>
                <p className="text-xs font-medium text-sage-400 max-w-lg mx-auto">
                    Start with a conversation &mdash; tell Nani what's troubling you and she'll suggest the right healing tools, or pick from these quick healing rituals below.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Nutri Heal Card */}
                <RitualCard
                    title="Nutri Heal"
                    subtitle="Kitchen Pharmacy"
                    icon={<Utensils size={24} />}
                    gradient="from-orange-50 to-amber-50 hover:to-amber-100"
                    iconBg="bg-orange-100 text-orange-600"
                    prompts={getPrompts('DIET')}
                    onClick={() => handleCardClick('DIET')}
                    onPromptClick={onSelect}
                />

                {/* Yoga Aid Card */}
                <RitualCard
                    title="Yoga Aid"
                    subtitle="Movement Medicine"
                    icon={<Flower2 size={24} />}
                    gradient="from-pink-50 to-rose-50 hover:to-rose-100"
                    iconBg="bg-pink-100 text-pink-600"
                    prompts={getPrompts('YOGA')}
                    onClick={() => handleCardClick('YOGA')}
                    onPromptClick={onSelect}
                />

                {/* Botanical Rx Card */}
                <RitualCard
                    title="Botanical Rx"
                    subtitle="Plant Wisdom"
                    icon={<Leaf size={24} />}
                    gradient="from-blue-50 to-indigo-50 hover:to-indigo-100"
                    iconBg="bg-blue-100 text-blue-600"
                    prompts={getPrompts('REMEDY')}
                    onClick={() => handleCardClick('REMEDY')}
                    onPromptClick={onSelect}
                />
            </div>

            {/* Interactive Popup Modal */}
            <AnimatePresence>
                {activePopup && popupData && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-sage-900/40 backdrop-blur-sm p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative"
                        >
                            <button
                                onClick={() => setActivePopup(null)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X size={20} />
                            </button>

                            <div className={`h-2 w-full bg-${popupData.color}-500`}></div>

                            <div className="p-8">
                                <div className={`w-12 h-12 rounded-2xl bg-${popupData.color}-50 flex items-center justify-center mb-4`}>
                                    {popupData.icon}
                                </div>

                                <h4 className="text-xl font-serif font-bold text-gray-900 mb-2">{popupData.title}</h4>
                                <p className="text-gray-600 mb-6 leading-relaxed">{popupData.question}</p>

                                <div className="relative">
                                    <textarea
                                        value={popupInput}
                                        onChange={(e) => setPopupInput(e.target.value)}
                                        placeholder="Type here..."
                                        className={`w-full bg-gray-50 border border-gray-200 rounded-xl p-4 pr-12 focus:outline-none focus:ring-2 focus:ring-${popupData.color}-500/20 focus:border-${popupData.color}-500 transition-all resize-none h-24`}
                                        autoFocus
                                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSubmitPopup())}
                                    />
                                    <button
                                        onClick={handleSubmitPopup}
                                        disabled={!popupInput.trim()}
                                        className={`absolute bottom-3 right-3 p-2 rounded-lg bg-${popupData.color}-500 text-white shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 transition-all`}
                                    >
                                        <ArrowRight size={18} />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

interface RitualCardProps {
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    gradient: string;
    iconBg: string;
    prompts: string[];
    onClick: () => void;
    onPromptClick: (text: string) => void;
}

const RitualCard: React.FC<RitualCardProps> = ({ title, subtitle, icon, gradient, iconBg, prompts, onClick, onPromptClick }) => (
    <div className={`
        group relative p-6 rounded-[2rem] border border-white/50 shadow-sm hover:shadow-xl
        bg-gradient-to-br ${gradient}
        transition-all duration-300 cursor-pointer
        flex flex-col h-full
    `}
        onClick={onClick}
    >
        <div className="flex items-start justify-between mb-8">
            <div className={`p-3.5 rounded-2xl ${iconBg} shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                {icon}
            </div>
            <div className="bg-white/60 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight size={16} className="text-gray-400" />
            </div>
        </div>

        <div className="mb-6">
            <h4 className="font-serif font-bold text-lg text-gray-900 mb-1">{title}</h4>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{subtitle}</p>
        </div>

        <div className="mt-auto space-y-2">
            {prompts.map((prompt, idx) => (
                <button
                    key={idx}
                    onClick={(e) => {
                        e.stopPropagation();
                        onPromptClick(prompt);
                    }}
                    className="w-full text-left px-3 py-2 bg-white/60 hover:bg-white rounded-lg text-xs font-medium text-gray-600 hover:text-sage-700 transition-colors flex items-center justify-between group/prompt"
                >
                    <span className="truncate mr-2">{prompt}</span>
                    <Sparkles size={10} className="opacity-0 group-hover/prompt:opacity-100 text-yellow-500 transition-opacity" />
                </button>
            ))}
        </div>
    </div>
);

export default DailyRituals;
